import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { HealthScoreRing } from '@/components/shared/health-score-ring';
import { SeverityBadge } from '@/components/dashboard/severity-badge';
import { StatsCard } from '@/components/dashboard/stats-card';
import {
  ArrowRight,
  ExternalLink,
  Clock,
  Shield,
  AlertTriangle,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Bot,
  FileText,
  Settings,
  Info,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import type { Severity, ScanStatus, FindingCounts } from '@nirvik/shared';
import { SCAN_TYPE_LABELS } from '@nirvik/shared';

interface ProjectData {
  projectId: string;
  name: string;
  targetUrl: string;
  description?: string;
  healthScore: number | null;
  findingCounts: FindingCounts;
  lastScanAt: string | null;
}

interface ScanData {
  scanId: string;
  scanType: string;
  status: ScanStatus;
  createdAt: string;
  duration: number | null;
  findingCounts?: FindingCounts;
}

interface AgentRun {
  runId: string;
  agentType: string;
  status: ScanStatus;
  createdAt: string;
  duration: number | null;
  findingSummary: { total: number; critical: number; high: number; medium: number; low: number; info: number };
}

interface FindingData {
  findingId: string;
  severity: Severity;
  title: string;
  description: string;
  category: string;
  status: string;
}

type ActivityItem =
  | { kind: 'agent'; data: AgentRun }
  | { kind: 'scan'; data: ScanData };

const AGENT_TYPE_LABELS: Record<string, string> = {
  'code-vuln': 'Code Vulnerability Scanner',
  dependency: 'Dependency Scanner',
  education: 'Security Education',
  'secret-scanner': 'Secret Scanner',
  'license-compliance': 'License Compliance',
  'container-security': 'Container Security',
  'iac-scanner': 'Infrastructure as Code',
  'api-security': 'API Security',
  sast: 'SAST Engine',
  'crypto-audit': 'Cryptography Audit',
  'access-control': 'Access Control Analysis',
  'data-privacy': 'Data Privacy',
  'supply-chain': 'Supply Chain Security',
  'cloud-config': 'Cloud Configuration',
  'threat-model': 'AI Threat Model',
  compliance: 'Compliance Mapping',
  'pentest-recon': 'Attack Surface Recon',
  'malware-detect': 'Malicious Code Detection',
  'config-audit': 'Configuration Audit',
  'network-security': 'Network Security',
  'incident-response': 'Incident Response Readiness',
  'security-score': 'Security Scorecard',
  'cicd-gate': 'CI/CD Security Gate',
};

const statusStyles: Record<ScanStatus, { bg: string; text: string; dot: string }> = {
  completed: { bg: 'bg-green-50 dark:bg-green-950/40', text: 'text-green-700 dark:text-green-400', dot: 'bg-green-500' },
  running: { bg: 'bg-blue-50 dark:bg-blue-950/40', text: 'text-blue-700 dark:text-blue-400', dot: 'bg-blue-500' },
  failed: { bg: 'bg-red-50 dark:bg-red-950/40', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  queued: { bg: 'bg-gray-50 dark:bg-muted/50', text: 'text-muted-foreground dark:text-muted-foreground', dot: 'bg-gray-400' },
  cancelled: { bg: 'bg-gray-50 dark:bg-muted/50', text: 'text-muted-foreground dark:text-muted-foreground', dot: 'bg-gray-400' },
};

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getActivityFindingCount(item: ActivityItem): number {
  if (item.kind === 'agent') {
    return item.data.findingSummary.total;
  }
  return item.data.findingCounts?.total ?? 0;
}

function StatusIcon({ status, className }: { status: ScanStatus; className?: string }) {
  if (status === 'running') return <Loader2 className={cn('h-4 w-4 animate-spin', className)} />;
  if (status === 'completed') return <CheckCircle2 className={cn('h-4 w-4', className)} />;
  if (status === 'failed') return <XCircle className={cn('h-4 w-4', className)} />;
  return <Clock className={cn('h-4 w-4', className)} />;
}

export function ProjectDetailPage() {
  const { orgId, portfolioId, projectId } = useParams<{ orgId: string; portfolioId: string; projectId: string }>();
  const enabled = !!orgId && !!portfolioId && !!projectId;

  const { data, isLoading, error } = useQuery({
    queryKey: ['project', orgId, portfolioId, projectId],
    queryFn: () => apiClient.getProject(orgId!, portfolioId!, projectId!),
    enabled,
  });

  const { data: scansRes } = useQuery({
    queryKey: ['scans', orgId, portfolioId, projectId],
    queryFn: () => apiClient.listScans(orgId!, portfolioId!, projectId!),
    enabled,
  });

  const { data: agentRunsRes } = useQuery({
    queryKey: ['agentRuns', orgId, portfolioId, projectId],
    queryFn: () => apiClient.get(`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/runs`),
    enabled,
  });

  const { data: findingsRes } = useQuery({
    queryKey: ['findings', orgId, portfolioId, projectId],
    queryFn: () => apiClient.listFindings(orgId!, portfolioId!, projectId!),
    enabled,
  });

  const project = data?.data as ProjectData | undefined;
  const scans = (scansRes?.data as ScanData[] | undefined) || [];
  const agentRuns = ((agentRunsRes?.data as { runs?: AgentRun[] })?.runs as AgentRun[] | undefined) || [];
  const findings = (findingsRes?.data as FindingData[] | undefined) || [];
  const recentFindings = findings.filter((f) => f.status === 'open').slice(0, 5);

  // Build unified activity list sorted by date descending
  const activityItems: ActivityItem[] = [
    ...agentRuns.map((run): ActivityItem => ({ kind: 'agent', data: run })),
    ...scans.map((scan): ActivityItem => ({ kind: 'scan', data: scan })),
  ]
    .sort((a, b) => {
      const dateA = new Date(a.kind === 'agent' ? a.data.createdAt : a.data.createdAt).getTime();
      const dateB = new Date(b.kind === 'agent' ? b.data.createdAt : b.data.createdAt).getTime();
      return dateB - dateA;
    })
    .slice(0, 8);

  // Compute "Last Activity" — most recent between scans and agent runs
  const lastScanTime = project?.lastScanAt ? new Date(project.lastScanAt).getTime() : 0;
  const lastAgentTime = agentRuns.length > 0 ? new Date(agentRuns[0].createdAt).getTime() : 0;
  const lastActivityTime = Math.max(lastScanTime, lastAgentTime);
  const lastActivityLabel = lastActivityTime > 0
    ? formatRelativeTime(new Date(lastActivityTime).toISOString())
    : 'Never';

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-4 bg-gray-100 dark:bg-muted rounded w-32" />
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-muted rounded w-64" />
          <div className="h-4 bg-gray-100 dark:bg-muted rounded w-48" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="h-10 bg-gray-100 dark:bg-muted rounded w-10 mb-3" />
                <div className="h-6 bg-gray-200 dark:bg-muted rounded w-12 mb-1" />
                <div className="h-4 bg-gray-100 dark:bg-muted rounded w-20" />
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="space-y-6">
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-foreground mb-2">Project not found</h2>
          <p className="text-muted-foreground dark:text-muted-foreground">This project may have been deleted or you don&apos;t have access.</p>
        </div>
      </div>
    );
  }

  // Use server-side aggregated counts from the project record
  // (updated by both agent-engine and scan-engine after each run)
  const counts = project.findingCounts || { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 };

  return (
    <div className="space-y-8">
      {/* Project Header — on phones the title + URL block stacks ABOVE the
       * action-button row (md:flex-row from tablet up). Action row uses
       * flex-wrap so "Run Agent / Summary Report / Settings" spill across
       * two rows on narrow widths instead of clipping ("Sett..." overflow).
       * URL gets its own truncate span so very long domains get an ellipsis
       * rather than pushing the layout wider. */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-foreground break-words">{project.name}</h1>
          <p className="text-muted-foreground dark:text-muted-foreground flex items-center gap-1.5 mt-1 min-w-0">
            <ExternalLink className="h-4 w-4 shrink-0" />
            <span className="truncate">{project.targetUrl}</span>
          </p>
          {project.description && (
            <p className="text-sm text-muted-foreground dark:text-muted-foreground mt-2">{project.description}</p>
          )}
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
          <Link to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents`}>
            <Button size="lg">
              <Bot className="h-4 w-4 mr-2" />
              Run Agent
            </Button>
          </Link>
          <Link to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/report`}>
            <Button size="lg" variant="outline">
              <FileText className="h-4 w-4 mr-2" />
              Summary Report
            </Button>
          </Link>
          <Link to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/settings`}>
            <Button size="lg" variant="ghost">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
          </Link>
        </div>
      </div>

      {/* Score + Stats Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-3 p-8 flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 via-saffron/10 to-chakra-navy/10 dark:from-primary/15 dark:via-saffron/15 dark:to-chakra-navy/20 border-primary/20 dark:border-primary/30">
          <div className="flex items-center gap-1.5 mb-4">
            <h2 className="text-sm font-bold text-foreground uppercase tracking-widest">Security Score</h2>
            <Link
              to="/my/learn/scoring"
              title="How is this calculated? — Open the scoring guide"
              aria-label="How is the security score calculated?"
              className="text-muted-foreground hover:text-primary-strong transition-colors"
            >
              <Info className="h-4 w-4" />
            </Link>
          </div>
          <HealthScoreRing score={project.healthScore} size={140} strokeWidth={9} />
        </Card>

        <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatsCard
            icon={AlertTriangle}
            label="Total Findings"
            value={counts.total}
            href={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings`}
            iconColor="text-orange-600 dark:text-orange-400"
            iconBg="bg-orange-100 dark:bg-orange-900/50"
            accent="orange"
          />
          <StatsCard
            icon={Search}
            label="Critical"
            value={counts.critical}
            href={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings`}
            iconColor="text-red-600 dark:text-red-400"
            iconBg="bg-red-100 dark:bg-red-900/50"
            accent="red"
            trend={counts.critical > 0 ? { direction: 'up', label: `${counts.critical}` } : undefined}
          />
          <StatsCard
            icon={Bot}
            label="Agent Runs"
            value={agentRuns.length}
            href={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents`}
            iconColor="text-indigo-600 dark:text-indigo-400"
            iconBg="bg-indigo-100 dark:bg-indigo-900/50"
            accent="indigo"
          />
          <StatsCard
            icon={Clock}
            label="Last Activity"
            value={lastActivityLabel}
            href={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents`}
            iconColor="text-blue-600 dark:text-blue-400"
            iconBg="bg-blue-100 dark:bg-blue-900/50"
            accent="blue"
          />
        </div>
      </div>

      {/* Severity Breakdown */}
      {counts.total > 0 && (
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-700 dark:text-foreground uppercase tracking-wide">Findings by Severity</h2>
            <Link
              to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings`}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
            >
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-3">
            {(['critical', 'high', 'medium', 'low'] as const)
              .map((severity) => ({ severity, count: counts[severity] ?? 0 }))
              .map(({ severity, count }) => (
                <SeverityBadge key={severity} severity={severity} count={count} />
              ))}
          </div>
        </Card>
      )}

      {/* Unified Recent Activity */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-800 dark:text-foreground uppercase tracking-wide">Recent Activity</h2>
          <div className="flex items-center gap-4">
            <Link
              to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents`}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
            >
              All agent runs <ArrowRight className="h-3 w-3" />
            </Link>
            <Link
              to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans`}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
            >
              All scans <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
        {activityItems.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-sm text-muted-foreground dark:text-muted-foreground">No activity yet.</p>
            <Link to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents`}>
              <Button size="sm" className="mt-3">
                <Bot className="h-4 w-4 mr-2" />
                Run First Agent
              </Button>
            </Link>
          </Card>
        ) : (
          <div className="flex flex-col gap-3">
            {activityItems.map((item) => {
              const isAgent = item.kind === 'agent';
              const status = item.data.status;
              const st = statusStyles[status] || statusStyles.queued;
              const createdAt = item.data.createdAt;
              const duration = item.data.duration;
              const findingCount = getActivityFindingCount(item);

              const label = isAgent
                ? (AGENT_TYPE_LABELS[item.data.agentType] ?? item.data.agentType)
                : (SCAN_TYPE_LABELS[(item.data as ScanData).scanType as keyof typeof SCAN_TYPE_LABELS] ?? (item.data as ScanData).scanType);

              const linkTo = isAgent
                ? `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/agents/runs/${(item.data as AgentRun).runId}`
                : `/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/scans/${(item.data as ScanData).scanId}`;

              const TypeIcon = isAgent ? Bot : Shield;

              return (
                <Link key={isAgent ? (item.data as AgentRun).runId : (item.data as ScanData).scanId} to={linkTo} className="block">
                  <Card className="p-4 hover:bg-gray-50 dark:hover:bg-muted/50 transition-colors cursor-pointer">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center shrink-0', st.bg)}>
                          {status === 'running' ? (
                            <Loader2 className={cn('h-4 w-4 animate-spin', st.text)} />
                          ) : (
                            <TypeIcon className={cn('h-4 w-4', st.text)} />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-foreground truncate">
                            {label}
                          </p>
                          <p className="text-xs text-muted-foreground dark:text-muted-foreground flex items-center gap-1.5 mt-0.5">
                            <span className={cn(
                              'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide',
                              isAgent
                                ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400'
                                : 'bg-gray-100 text-muted-foreground dark:bg-muted dark:text-muted-foreground'
                            )}>
                              {isAgent ? 'Agent' : 'Scan'}
                            </span>
                            <span className="text-foreground dark:text-muted-foreground">·</span>
                            {formatRelativeTime(createdAt)}
                            {duration !== null && (
                              <>
                                <span className="text-foreground dark:text-muted-foreground">·</span>
                                {formatDuration(duration)}
                              </>
                            )}
                            {findingCount > 0 && (
                              <>
                                <span className="text-foreground dark:text-muted-foreground">·</span>
                                <span className="text-orange-600 dark:text-orange-400">{findingCount} finding{findingCount !== 1 ? 's' : ''}</span>
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <span className={cn('text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1.5 shrink-0', st.bg, st.text)}>
                        <span className={cn('h-1.5 w-1.5 rounded-full', st.dot, status === 'running' && 'animate-pulse')} />
                        <span className="capitalize">{status}</span>
                      </span>
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Open Findings — full width */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-800 dark:text-foreground uppercase tracking-wide">Open Findings</h2>
          <Link
            to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings`}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1"
          >
            View all <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        {recentFindings.length === 0 ? (
          <Card className="p-8 text-center">
            <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-900 dark:text-foreground">No open findings</p>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">Run an agent to check for issues</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {recentFindings.map((finding) => (
              <Link key={finding.findingId} to={`/orgs/${orgId}/portfolios/${portfolioId}/projects/${projectId}/findings`} className="block">
                <Card className="p-4 hover:bg-gray-50 dark:hover:bg-muted/50 transition-colors cursor-pointer">
                  <div className="flex items-start gap-2">
                    <SeverityBadge severity={finding.severity} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-foreground line-clamp-1">{finding.title}</p>
                      <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-0.5 line-clamp-1">{finding.description}</p>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
